import React, { useCallback, useMemo } from "react";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  getActiveTemplateOptionsGroupedByType,
  getFnForGetIssueTemplateById,
  GetIssueTemplateByIdFn,
  IssueTemplateOptionGroup,
} from "@adsk/acc-issues-core/state/redux/ducks/issueTemplates";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useRecentlyUsedTemplateIds } from "../../utils/hooks/useRecentlyUsedTemplateIds";
import {
  useAutocomplete,
  AutocompleteMenu,
  AutocompleteControl,
  DefaultItem,
  isGroupHeader,
  MenuGroupHeader,
  MenuItem,
} from "@adsk/alloy-react-dropdown-v2";
import { InputItemGroup } from "@adsk/alloy-react-dropdown-v2/es/types";
import { DropdownFooter } from "@adsk/acc-issues-common/components/primitive/DropdownFooter";

const RECENTLY_USED_PREFIX = "RECENTLY-USED-";

export const useIssueTemplateDisplayValue = (
  issueTemplateId: string | undefined
) => {
  const getIssueTemplateById: GetIssueTemplateByIdFn = useSelector(
    getFnForGetIssueTemplateById
  );
  if (issueTemplateId) {
    const template = getIssueTemplateById(issueTemplateId);
    return template?.issueAttributes.title;
  }
};

export const IssueTemplatesDropdown = ({
  selectedIssueTemplateId,
  onChange,
}: {
  selectedIssueTemplateId?: string;
  onChange?: (templateId: string | undefined) => void;
}) => {
  const { T } = useTranslations();
  const { recentlyUsedTemplatesToDisplay, addTemplateIdToRecentlyUsed } =
    useRecentlyUsedTemplateIds({ selectedTemplateId: selectedIssueTemplateId });

  const recentlyUsedTemplatesMemo = useMemo(
    () => recentlyUsedTemplatesToDisplay,
    [recentlyUsedTemplatesToDisplay]
  );

  const generateRecentlyUsedTemplateObject = useCallback(
    () => {
      const options = recentlyUsedTemplatesMemo.map(template => ({
        label: template.issueAttributes.title,
        // we need to give it a unique value so as not to mess up the picker
        value: RECENTLY_USED_PREFIX + template.id,
      }));

      return {
        label: T.translate("templateSelector.recentlyUsed") as string,
        // TODO: remove when component migration is finalized
        options,
        // we just need any string here, to be used as the option key
        typeId: "recently-selected-templates",
        items: options,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // if we just use the selector, we end up with a reference to the same list
  // in different renders and unshifting onto it messes things up.
  // So, we copy the list into a new reference
  const groupedOptions: IssueTemplateOptionGroup[] = [
    ...useSelector(getActiveTemplateOptionsGroupedByType),
  ];

  // add the recently used section if there's no search
  if (recentlyUsedTemplatesMemo.length) {
    groupedOptions.unshift(generateRecentlyUsedTemplateObject());
  }

  const handleChange = useCallback(
    item => {
      let value;
      if (item) {
        const { selectedItem } = item;
        value = selectedItem.value as string;
        if (value.startsWith(RECENTLY_USED_PREFIX)) {
          // get the template ID from the option value that includes the recently used prefix,
          // because we need unique values for options
          value = value.slice(RECENTLY_USED_PREFIX.length);
        }
        addTemplateIdToRecentlyUsed(value);
      }
      onChange && onChange(value);
    },
    [onChange, addTemplateIdToRecentlyUsed]
  );

  const getSelectedItem = useCallback((): DefaultItem | undefined => {
    if (!selectedIssueTemplateId) return;

    // new dropdown requires referential equality for the selected item
    for (const group of groupedOptions) {
      const selected = group.items.find(
        option =>
          `RECENTLY-USED-${selectedIssueTemplateId}` === option.value ||
          selectedIssueTemplateId === option.value
      );

      if (selected) {
        return selected;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO: remove when component migration is finalized
  groupedOptions.forEach(
    (group: IssueTemplateOptionGroup) => (group.items = group.options)
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const selectedItem = useMemo(getSelectedItem, [selectedIssueTemplateId]);

  const { menuProps, controlProps } = useAutocomplete({
    groupedItems: groupedOptions as InputItemGroup<DefaultItem>[],
    selectedItem,
    onSelectedItemChange: handleChange,
    id: "template-dropdown",
  });

  return (
    <>
      <AutocompleteControl
        {...controlProps}
        placeholder={
          T.translate("issueCreation.template.placeholder") as string
        }
        data-testid={`dropdown-control`}
      />
      <AutocompleteMenu
        {...menuProps}
        renderFooter={() =>
          DropdownFooter({
            isClearable: true,
            handleChange,
            shouldDisplay: !!selectedIssueTemplateId,
          })
        }
        data-testid={`dropdown-menu`}
      >
        {menuProps.menuItems.map((itemOrGroupHeader, i) =>
          isGroupHeader(itemOrGroupHeader) ? (
            <MenuGroupHeader key={i} data-testid={"dropdown-header"}>
              {itemOrGroupHeader.label}
            </MenuGroupHeader>
          ) : (
            <MenuItem
              key={i}
              {...menuProps.getItemProps({ item: itemOrGroupHeader })}
            />
          )
        )}
      </AutocompleteMenu>
    </>
  );
};
